import React from 'react'

import { CheckCircle } from '@phosphor-icons/react'
import { Button, Column, Row, Text } from 'Components/UI'

import { MEMBERSHIP_PACKAGE_KIND } from 'Constants/MembershipPackages'

import {
  MembershipFeatureRecordFieldsFragment,
  MembershipPackageRecordFieldsFragment,
} from 'GraphQL/DatoCMS/TypedDocuments'

import { Card, Cell, Container, Divider, GridContainer, Price } from './styles'

function isPartnerPackage(kind?: string) {
  return (
    kind === MEMBERSHIP_PACKAGE_KIND.BLACK_PARTNER ||
    kind === MEMBERSHIP_PACKAGE_KIND.DIAMOND_PARTNER
  )
}

function getCellContent(
  membershipPackage: MembershipPackageRecordFieldsFragment,
  featureId: string,
) {
  const entry = membershipPackage.featureEntries.find(
    featureEntry => featureEntry.feature?.id === featureId,
  )

  if (!entry) {
    return (
      <Text disabled medium screen18>
        -
      </Text>
    )
  }

  if (entry.description) {
    return (
      <Text medium screen18>
        {entry.description}
      </Text>
    )
  }

  return <CheckCircle size={25} weight="fill" />
}

type Props = {
  packages: MembershipPackageRecordFieldsFragment[]
  features: MembershipFeatureRecordFieldsFragment[]
  secondary?: boolean
  onAction?: () => void
}

function TeeOffCorporateMembershipPackagesTable({
  packages,
  features,
  secondary,
  onAction,
}: Props) {
  const featuresColumnWidth = packages.length > 3 ? '1fr' : '400px'
  const gridTemplateColumns = `${featuresColumnWidth} repeat(${packages.length}, 1fr)`

  return (
    <Container>
      <GridContainer gridTemplateColumns={gridTemplateColumns}>
        <Column gap={11} spaceBetween>
          <Text disabled medium mt={18} screen24>
            Features
          </Text>

          <Divider />
        </Column>

        {packages.map(membershipPackage => (
          <Column gap={11} height="175px" key={membershipPackage.id}>
            <Card
              mt="auto"
              mx={3}
              secondary={secondary}
              tertiary={isPartnerPackage(membershipPackage.kind)}
            >
              <Text bold center preWrap screen24>
                {membershipPackage.title}
              </Text>

              <Button large onClick={onAction}>
                Enquire Now
              </Button>
            </Card>

            <Divider />
          </Column>
        ))}
      </GridContainer>

      {features.map(feature => (
        <Column key={feature.id}>
          <GridContainer gridTemplateColumns={gridTemplateColumns}>
            <Column height="52px" spaceBetween>
              <Row alignCenter fullHeight>
                <Text important medium screen18>
                  {feature.title}
                </Text>
              </Row>

              <Divider secondary />
            </Column>

            {packages.map(membershipPackage => (
              <Column height="52px" key={membershipPackage.id}>
                <Cell kind={membershipPackage.kind}>
                  {getCellContent(membershipPackage, feature.id)}
                </Cell>

                <Divider
                  secondary={!isPartnerPackage(membershipPackage.kind)}
                />
              </Column>
            ))}
          </GridContainer>
        </Column>
      ))}

      <GridContainer gridTemplateColumns={gridTemplateColumns}>
        <div />

        {packages.map(membershipPackage => (
          <Price
            key={membershipPackage.id}
            secondary={
              membershipPackage.kind === MEMBERSHIP_PACKAGE_KIND.CLUB_PLATINUM
            }
            tertiary={isPartnerPackage(membershipPackage.kind)}
          >
            {membershipPackage.isPriceUponApplication ? (
              <Text bold center inverse screen18>
                Upon Application
              </Text>
            ) : (
              <>
                <Text bold inverse screen18>
                  ${membershipPackage.price}
                </Text>

                <Text inverse regular screen18>
                  +GST P/A
                </Text>
              </>
            )}
          </Price>
        ))}
      </GridContainer>
    </Container>
  )
}

export default TeeOffCorporateMembershipPackagesTable
